// components/HowItWorks.js
// Yuan Wang
// Copyright American Marine Research Company LLC 2019

import React, { Fragment, useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import Image from 'material-ui-image';
import shopping_bag from '../assets/icons/basic.png'
import magic_wand from '../assets/icons/basic.png'
import present_box from '../assets/icons/basic.png'
import tailor_at_work from '../assets/icons/basic.png'
import fit_delivered from '../assets/icons/basic.png'
import order_crop from '../assets/icons/basic.png'

import { makeStyles, createStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => 
  createStyles({
    logo: {
      maxHeight: 50,
      maxWidth: '100%',
      marginBottom: theme.spacing(1)
    },
    growGrid: {
      flexGrow: 1,
      flexDirection: 'column',
      display: 'flex',
      justifyContent: 'flex-start',
      marginBottom: theme.spacing(2)
    },
    demoImageContainer: {
      minHeight: 500,
      display: 'flex',
      justifyContent: 'center',
      borderRadius: theme.shape.borderRadius
    },
    iconContainer: {
      minWidth: 80,
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(1)
    },
    subtitle: {
      marginBottom: theme.spacing(4)
    },
    litemText: {

    },
    blockContentContainer: {
      display: 'flex',
      flexDirection: 'row'
    },
    blockContentContainer_inactive: {
      display: 'flex',
      flexDirection: 'row',
      opacity: 0.4, '&:hover': {
        opacity: 1
      }
    },
    blockTextBox: {
      display: 'flex',
      flexDirection: 'column'
    },
    contentContainer: {
      display: 'flex',
      alignItems: "flex-end"
    },
    textBox: {
      flexDirection: 'row',
      display: 'flex',
      alignItems: "flex-start"
    },
    index: {
      fontWeight: 900,
    }, 
    imageContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 200,
      height: 200,
    },
    image_fit_delivered: {
      height: '50%',
      maxWidth: 400,
      maxHeight: '50%'
    },
    image_tailor_at_work: {
      height: '50%',
      maxWidth: 400,
      maxHeight: '50%'
    },
    image: {
      // width: '70%'
    },
    gif: {
      width: 240,
      borderRadius: theme.shape.borderRadius,
    }
  })
)

const STEPS = [
  {
    title: "Shop online as usual",
    subtitle: "Shop your favorite brands. Leave the sizing to us.",
    media: ({ classes }) => (
      <img
        alt="shopping icon"
        className={classes.logo}
        src={shopping_bag}
      />
    ),
    poster: ({ classes }) => (
      <div className={classes.demoImageContainer}>
        <div className={classes.gif}>
          <Image
            className={classes.gif}
            imageStyle={{height: 480, maxWidth: '90vw', objectFit: 'contain'}}
            src={order_crop}
            alt="ordering demo on iOS"
            aspectRatio={(2)}
            />
        </div>
      </div>
    )
  },
  {
    title: "Your fairy god tailor fixes everything",
    subtitle: "Certified pro who handles alteration & sizing needs",
    media: ({ classes }) => (
      <img
        alt="magic wand"
        className={classes.logo}
        src={magic_wand}
      />
    ),
    poster: ({ classes }) => (
      <Hidden only={["xs"]}>
        <Image
          className={classes.image}
          imageStyle={{minHeight: '80%', maxHeight: '80%', objectFit: 'contain'}}
          src={tailor_at_work}
          alt="tailor at work"
          aspectRatio={(619/570)}
          />
      </Hidden>
    )
  },
  {
    title: "Get the right fit delivered",
    subtitle: "No need to stress about sizing, fit or returns... ever again!",
    media: ({ classes }) => (
      <img
        alt="present"
        className={classes.logo}
        src={present_box}
      />
    ),
    poster: ({ classes }) => (
      <Hidden only={["xs"]}>
        <Image
          className={classes.image}
          imageStyle={{minHeight: '80%', maxHeight: '80%', objectFit: 'contain'}}
          src={fit_delivered}
          alt="package delivery"
          aspectRatio={(590/533)}
          />
      </Hidden>
    )
  }
]

function Block({ media, title, subtitle, active, poster }) {
  const classes = useStyles()
  return (
    <Fragment>
      <Hidden only={["sm", "md", "lg", "xl"]}>
        { poster({ classes }) }
        <div className={classes.blockContentContainer}>
          <div className={classes.iconContainer}>
            { media({ classes }) }
          </div>
          <div className={classes.blockTextBox}>
            <Typography 
              variant="h6" 
              className={classes.text} 
              gutterBottom>
              { title }
            </Typography>
            <Typography 
              variant="body1" 
              className={classes.subtitle} 
              gutterBottom>
              { subtitle }
            </Typography>
          </div>
        </div>
      </Hidden>
      <Hidden only={["xs"]}>
        <div className={active ? classes.blockContentContainer : classes.blockContentContainer_inactive}>
          <div className={classes.iconContainer}>
            { media({ classes }) }
          </div>
          <div className={classes.blockTextBox}>
            <Typography 
              variant="h6" 
              className={classes.text} 
              gutterBottom>
              { title }
            </Typography>
            <Typography 
              variant="body1" 
              className={classes.text} 
              gutterBottom>
              { subtitle }
            </Typography>
          </div>
        </div>
      </Hidden>
    </Fragment>
  )
}

const FRAME_TIMES = [ 10000, 2000, 2000 ]

export default function HowItWorks() {
  const classes = useStyles()
  const [ activeIndex, setActiveIndex ] = useState(0)

  const nextActiveIndex = (index) => {
    const nextIndex = (index + 1) % 3
    setActiveIndex(nextIndex)
    setTimeout(() => nextActiveIndex(nextIndex), FRAME_TIMES[nextIndex])
  }

  useEffect(() => {
    const timeout = setTimeout(() => nextActiveIndex(0), FRAME_TIMES[0]);
    // nextActiveIndex(0)
    // Specify how to clean up after this effect:
    return function cleanup() {
      clearTimeout(timeout)
    };
  }, []);

  return (
    <Fragment>
      <Grid spacing={2} container>
        <Grid item xs={12} sm={7} className={classes.growGrid}>
          <br/>
          {STEPS.map((item, index) => (
            <a 
              key={`step-${index}`} 
              onClick={() => setActiveIndex(index)}>
              { index != 0 && <Hidden only={["xs"]}><br/><br/></Hidden> }
              <Block {...item} active={activeIndex === index}/>
            </a>
          ))}
        </Grid>
        <Grid item xs={12} sm={5} className={classes.growGrid}>
          <Hidden only={['xs']}>
            { STEPS[activeIndex].poster({ classes }) }
          </Hidden>
        </Grid>
      </Grid>
    </Fragment>
  )
}
